import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getRompslompInvoiceDetail, getRompslompInvoices, putRompslompPairDecision, sendRompslompInvoice } from '../../../services/RompslompService';
import { SendConfirmModal } from './RompslompSending';
import { apiMessage, euro } from './rompslompUtils';

const STATUSES = ['ready', 'not_ready', 'net_zero', 'sent', 'verified', 'needs_attention', 'not_sent'];
const matches = (row, status) => (status === 'all' ? true : (status === 'not_sent' ? row.status.startsWith('not_sent') : row.status === status));

// Plain text, no badges: the status is one line, the reason one sentence the person can act on.
export function statusLabel(t, row) {
    if (row.status === 'not_sent_decision') return t('rompslomp.invoices.status.not_sent_decision', { user: row.reason_params?.user, date: row.reason_params?.date });
    return t(`rompslomp.invoices.status.${row.status}`);
}

export function reasonText(t, row) {
    if (row.status === 'needs_attention' || row.status === 'sent') {
        return row.push?.last_error || (row.status === 'sent' ? t('rompslomp.invoices.reason.sentUnchecked') : t('rompslomp.invoices.reason.seeLog'));
    }
    if (!row.reason_key || row.status === 'not_sent_decision') return '';
    const params = row.reason_params || {};
    return t(`rompslomp.invoices.reason.${row.reason_key}`, { ...params, missing: (params.missing || []).join(', '), invoice: params.invoice || '' });
}

function PairDecision({ pair, onChoose }) {
    const { t } = useTranslation();
    const d = pair.decision;
    if (pair.locked) {
        return <p className="mb-0" data-testid={`pair-locked-${pair.order_id}`}>{t('rompslomp.pairs.locked', { decision: t(`rompslomp.pairs.decisions.${d.decision}`), user: d.user, date: (d.at || '').slice(0, 10) })}</p>;
    }
    return (
        <div data-testid={`pair-${pair.order_id}`}>
            <p className="mb-2">
                {d ? t('rompslomp.pairs.current', { decision: t(`rompslomp.pairs.decisions.${d.decision}`), user: d.user, date: (d.at || '').slice(0, 10) }) : t('rompslomp.invoices.reason.netZero')}
                {d?.note ? ` ${t('rompslomp.pairs.note', { note: d.note })}` : ''}
            </p>
            <div className="rompslomp-pair-actions">
                <Button size="sm" variant="outline-danger" disabled={d?.decision === 'send_both'} onClick={() => onChoose(pair, 'send_both')} data-testid={`send-both-${pair.order_id}`}>
                    {t('rompslomp.pairs.sendBoth')}
                </Button>
                <Button size="sm" variant="outline-secondary" disabled={d?.decision === 'dont_send'} onClick={() => onChoose(pair, 'dont_send')} data-testid={`dont-send-${pair.order_id}`}>
                    {t('rompslomp.pairs.dontSend')}
                </Button>
            </div>
        </div>
    );
}

export default function RompslompInvoices({ sending, reloadSending }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [status, setStatus] = useState('all');
    const [kind, setKind] = useState('all');
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState({});            // id -> payload detail (loaded on demand)
    const [confirm, setConfirm] = useState(null);
    const [choice, setChoice] = useState(null);      // {pair, decision}
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try { setData((await getRompslompInvoices()).data); } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); }
    }, [t]);
    useEffect(() => { load(); }, [load]);

    const pairs = useMemo(() => Object.fromEntries((data?.pairs || []).map((p) => [p.order_id, p])), [data]);

    // one group per order (the invoice and its credit notes together), a group is shown when any of its documents matches the filters
    const groups = useMemo(() => {
        const q = query.trim().toLowerCase();
        const by = new Map();
        (data?.documents || []).forEach((r) => { if (!by.has(r.order_id)) by.set(r.order_id, []); by.get(r.order_id).push(r); });
        return [...by.entries()].map(([orderId, docs]) => ({ orderId, docs }))
            .filter(({ docs }) => docs.some((r) => matches(r, status) && (kind === 'all' || r.kind === kind)
                && (!q || `${r.number} ${r.order_name || ''}`.toLowerCase().includes(q))));
    }, [data, status, kind, query]);

    const togglePayload = async (row) => {
        if (open[row.id]) { setOpen((o) => ({ ...o, [row.id]: null })); return; }
        try { const d = (await getRompslompInvoiceDetail(row.id)).data; setOpen((o) => ({ ...o, [row.id]: d })); } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); }
    };
    const ask = async (row) => {
        try { setConfirm({ row, ...(await getRompslompInvoiceDetail(row.id)).data }); } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); }
    };
    const send = async () => {
        setBusy(true);
        try {
            const res = await sendRompslompInvoice(confirm.row.id, confirm.payload_hash);
            toast[res.data.result.ok ? 'success' : 'warning'](t(`rompslomp.log.status.${res.data.result.status}`));
            setConfirm(null);
        } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); } finally { setBusy(false); load(); reloadSending(); }
    };
    const decide = async (note) => {
        setBusy(true);
        try {
            setData((await putRompslompPairDecision(choice.pair.order_id, choice.decision, note)).data);
            toast.success(t('rompslomp.pairs.saved'));
            setChoice(null);
        } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); load(); } finally { setBusy(false); reloadSending(); }
    };

    if (!data) return <Spinner animation="border" />;
    const noteSchema = Yup.object({ note: Yup.string().max(300) });
    return (
        <>
            {sending.auto?.enabled && <Alert variant="info" data-testid="auto-on-note">{t('rompslomp.invoices.autoOnNote')}</Alert>}
            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                <Form.Select size="sm" className="rompslomp-filter" aria-label={t('rompslomp.invoices.filterStatus')} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status">
                    <option value="all">{t('rompslomp.invoices.allStatuses')}</option>
                    {STATUSES.map((s) => <option key={s} value={s}>{t(`rompslomp.invoices.status.${s}`)}</option>)}
                </Form.Select>
                <Form.Select size="sm" className="rompslomp-filter" aria-label={t('rompslomp.invoices.filterKind')} value={kind} onChange={(e) => setKind(e.target.value)} data-testid="filter-kind">
                    <option value="all">{t('rompslomp.invoices.allKinds')}</option>
                    <option value="invoice">{t('rompslomp.invoices.kinds.invoice')}</option>
                    <option value="credit_note">{t('rompslomp.invoices.kinds.credit_note')}</option>
                    <option value="marketing">{t('rompslomp.invoices.kinds.marketing')}</option>
                </Form.Select>
                <Form.Control size="sm" className="rompslomp-filter" type="search" placeholder={t('rompslomp.invoices.search')} aria-label={t('rompslomp.invoices.search')}
                    value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
            <Table className="rompslomp-table rompslomp-invoices" size="sm" data-testid="invoices-table">
                <thead><tr>
                    <th className="c-number">{t('rompslomp.invoices.number')}</th><th className="c-order">{t('rompslomp.invoices.order')}</th><th className="c-date">{t('rompslomp.invoices.date')}</th>
                    <th className="c-total">{t('rompslomp.invoices.total')}</th><th className="c-status">{t('rompslomp.invoices.statusTitle')}</th><th className="c-actions" />
                </tr></thead>
                {groups.length === 0 && <tbody><tr><td colSpan="6" className="text-center text-muted">{t('rompslomp.invoices.empty')}</td></tr></tbody>}
                {groups.map(({ orderId, docs }) => (
                    <tbody key={orderId} className="rompslomp-group" data-testid={`group-${orderId}`}>
                        {docs.map((r) => {
                            const reason = reasonText(t, r);
                            const detail = open[r.id];
                            const showPayload = (r.kind === 'invoice' || r.kind === 'credit_note') && !r.is_test;
                            return (
                                <React.Fragment key={r.id}>
                                    <tr className="inv-row" data-testid={`invoice-row-${r.number}`}>
                                        <td className="c-number" data-label={t('rompslomp.invoices.number')}>
                                            <strong>{r.number}</strong> <span className="text-muted">{t(`rompslomp.invoices.type.${r.kind}`)}</span>
                                            {r.credit_for ? <div className="small text-muted">{t('rompslomp.invoices.creditFor', { number: r.credit_for })}</div> : null}
                                        </td>
                                        <td className="c-order" data-label={t('rompslomp.invoices.order')}>{r.order_name || '-'}{r.source ? <div className="small text-muted">{r.source}</div> : null}</td>
                                        <td className="c-date" data-label={t('rompslomp.invoices.date')}>{r.date}</td>
                                        <td className="c-total" data-label={t('rompslomp.invoices.total')}>{euro(r.kind === 'credit_note' ? -r.total_cents : r.total_cents)}</td>
                                        <td className="c-status" data-label={t('rompslomp.invoices.statusTitle')}>
                                            <strong data-testid={`status-${r.number}`}>{statusLabel(t, r)}</strong>
                                            {reason ? <div className="small" data-testid={`reason-${r.number}`}>{reason}</div> : null}
                                        </td>
                                        <td className="c-actions">
                                            {showPayload && (
                                                <Button size="sm" variant="light" onClick={() => togglePayload(r)} data-testid={`payload-${r.number}`}>
                                                    {detail ? t('rompslomp.invoices.hidePayload') : t('rompslomp.invoices.showPayload')}
                                                </Button>
                                            )}
                                            {r.status === 'ready' && (
                                                <Button size="sm" variant="outline-danger" disabled={!sending.can_send} onClick={() => ask(r)} data-testid={`send-${r.number}`}>
                                                    {t('rompslomp.sending.sendThis')}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                    {detail && detail.payload && (
                                        <tr className="inv-payload"><td colSpan="6"><pre className="rompslomp-payload" data-testid={`payload-body-${r.number}`}>{JSON.stringify(detail.payload, null, 2)}</pre>
                                            {(detail.warnings || []).map((w) => <div key={w} className="small text-muted">{w}</div>)}</td></tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {pairs[orderId] && <tr className="inv-pair"><td colSpan="6"><PairDecision pair={pairs[orderId]} onChoose={(pair, decision) => setChoice({ pair, decision })} /></td></tr>}
                    </tbody>
                ))}
            </Table>
            <SendConfirmModal show={!!confirm} title={confirm ? `${t('rompslomp.sending.sendThis')}: ${confirm.row.number}` : ''}
                payload={confirm?.payload} hash={confirm?.payload_hash} busy={busy} onConfirm={send} onHide={() => setConfirm(null)} />
            <Modal show={!!choice} onHide={() => setChoice(null)} centered>
                <Formik initialValues={{ note: '' }} validationSchema={noteSchema} onSubmit={(v) => decide(v.note.trim())}>
                    {({ handleSubmit, handleChange, values }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <Modal.Header closeButton><Modal.Title>{choice ? t(`rompslomp.pairs.${choice.decision === 'send_both' ? 'sendBoth' : 'dontSend'}`) : ''}</Modal.Title></Modal.Header>
                            <Modal.Body>
                                <p data-testid="pair-confirm-text">{choice ? t(`rompslomp.pairs.explain.${choice.decision}`) : ''}</p>
                                <p className="text-muted small">{t('rompslomp.pairs.nothingSent')}</p>
                                <Form.Group controlId="pair-note">
                                    <Form.Label>{t('rompslomp.pairs.noteLabel')}</Form.Label>
                                    <Form.Control name="note" maxLength={300} value={values.note} onChange={handleChange} />
                                </Form.Group>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="light" onClick={() => setChoice(null)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
                                <Button type="submit" variant="danger" disabled={busy} data-testid="confirm-pair">{t('rompslomp.pairs.confirm')}</Button>
                            </Modal.Footer>
                        </Form>
                    )}
                </Formik>
            </Modal>
        </>
    );
}
